import { useEffect, useRef } from 'react';
const SCROLL_MARGIN = 16;
const isScrollable = overflow => overflow === 'auto' || overflow === 'scroll';

/**
 * Nearest ancestor that declares a scrollport, which for a passenger card is the desktop
 * card list or the mobile tab strip. Letting the browser pick the scroll target instead
 * also moves the seat map container, whose header and footer are siblings of the sliding
 * segment panel, so they get dragged along while the slide animation is still running.
 */
const getScrollPort = element => {
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    const {
      overflowX,
      overflowY
    } = window.getComputedStyle(parent);
    if (isScrollable(overflowX) || isScrollable(overflowY)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return undefined;
};

/**
 * Distance needed to bring `[start, end]` inside `[portStart, portEnd]`, zero when it
 * already fits. Card and scrollport are measured in the same transformed subtree, so the
 * difference stays correct while the segment panel is still sliding sideways.
 */
const getScrollOffset = (start, end, portStart, portEnd) => {
  if (start < portStart) {
    return start - portStart - SCROLL_MARGIN;
  }
  if (end > portEnd) {
    return end - portEnd + SCROLL_MARGIN;
  }
  return 0;
};

/**
 * Keeps the active passenger card visible, for instance when the seat map is reopened on
 * a specific traveler through "Change seat" in the summary. Only the list holding the card
 * scrolls, so the desktop card list and the mobile tab strip are both covered without the
 * surrounding seat map moving.
 */
export const useScrollActiveTravelerIntoView = isActive => {
  const ref = useRef(null);
  useEffect(() => {
    const card = ref.current;
    if (!isActive || !card) {
      return;
    }
    const scrollPort = getScrollPort(card);
    if (!scrollPort) {
      return;
    }
    const scrollCardIntoView = () => {
      var _scrollPort$scrollBy;
      const cardRect = card.getBoundingClientRect();
      const portRect = scrollPort.getBoundingClientRect();
      const left = getScrollOffset(cardRect.left, cardRect.right, portRect.left, portRect.right);
      const top = getScrollOffset(cardRect.top, cardRect.bottom, portRect.top, portRect.bottom);
      if (left === 0 && top === 0) {
        return;
      }
      (_scrollPort$scrollBy = scrollPort.scrollBy) === null || _scrollPort$scrollBy === void 0 || _scrollPort$scrollBy.call(scrollPort, {
        behavior: 'smooth',
        left,
        top
      });
    };
    scrollCardIntoView();

    /**
     * The card expands as it becomes the active one, so the first measurement is taken
     * against its collapsed height and leaves the last traveler short of the scrollport.
     * Re-measuring on every size change tops the scroll up, and since both rects are read
     * at the same instant the remaining offset stays correct mid animation.
     */
    const resizeObserver = new ResizeObserver(scrollCardIntoView);
    resizeObserver.observe(card);
    return () => resizeObserver.disconnect();
  }, [isActive]);
  return ref;
};
//# sourceMappingURL=useScrollActiveTravelerIntoView.js.map